import React from 'react';
import { useNotification } from '../context/NotificationContext';

const ToastContainer = () => {
  const { notifications, dismissNotification } = useNotification();

  return (
    <div className="fixed top-4 right-4 z-50 space-y-3 max-w-md">
      {notifications.map((notification) => (
        <Toast
          key={notification.id}
          notification={notification}
          onDismiss={() => dismissNotification(notification.id)}
        />
      ))}
    </div>
  );
};

const Toast = ({ notification, onDismiss }) => {
  const getToastStyles = (type) => {
    const base = 'rounded-lg shadow-lg border-l-4 p-4 pr-10 relative animate-slide-in';
    const styles = {
      info: 'bg-tech-card border-tech-accent',
      success: 'bg-tech-card border-tech-success',
      error: 'bg-tech-card border-tech-danger',
      warning: 'bg-tech-card border-yellow-500',
      osint_complete: 'bg-tech-card border-tech-accent',
    };
    return `${base} ${styles[type] || styles.info}`;
  };

  const getIcon = (type) => {
    const icons = {
      info: (
        <svg className="w-5 h-5 text-tech-accent" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
        </svg>
      ),
      success: (
        <svg className="w-5 h-5 text-tech-success" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
        </svg>
      ),
      error: (
        <svg className="w-5 h-5 text-tech-danger" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
        </svg>
      ),
      warning: (
        <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
        </svg>
      ),
      osint_complete: (
        <svg className="w-5 h-5 text-tech-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
        </svg>
      ),
    };
    return icons[notification.type] || icons.info;
  };

  return (
    <div className={getToastStyles(notification.type)}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {getIcon(notification.type)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-tech-text">
            {notification.title}
          </p>
          <p className="mt-1 text-sm text-tech-textMuted">
            {notification.message}
          </p>
          {notification.action && (
            <button
              onClick={notification.action.onClick}
              className="mt-2 text-sm font-medium text-tech-accent hover:text-tech-accent/80 transition"
            >
              {notification.action.label}
            </button>
          )}
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 text-tech-textMuted hover:text-tech-text transition"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>

      {/* Progress bar for persistent notifications */}
      {notification.persistent && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-tech-surface rounded-b-lg overflow-hidden">
          <div className="h-full bg-tech-accent animate-shrink"/>
        </div>
      )}

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
        .animate-shrink {
          animation: shrink 5s linear forwards;
        }
      `}</style>
    </div>
  );
};

export default ToastContainer;
